import { useEffect, useRef } from 'react'

import {
  Descriptions,
  Form,
  Header,
  Label,
  PrimaryButton,
  Space,
  StatusBadge,
  TertiaryButton,
  WithFormControlSelect,
  type StatusBadgeStatus,
} from '@procurify/ui'
import { type FormikProps, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { type RowCellMenuListItemProps } from '@webapp/components/DataTable'
import { UserAvatar } from '@webapp/components/UserAvatar'
import { CostSummary } from '@webapp/core/components/CostSummary'
import { useFormatDate } from '@webapp/core/components/FormattedDate/useFormatDate'
import { Price } from '@webapp/core/components/Price'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  AttachableTravelTicketSelect,
  type AttachableTravelTicketSelectValue,
} from '@webapp/modules/expense/components/AttachableTravelTicketSelect'
import {
  ExpenseItemTable,
  type ExpenseViewerRoleEnum,
} from '@webapp/modules/expense/components/ExpenseItemTable'
import {
  NextApproverSelect,
  type NextApproverSelectValue,
} from '@webapp/modules/expense/components/NextApproverSelect'
import {
  RequestablePrimaryCategorySelect,
  type RequestablePrimaryCategorySelectValue,
} from '@webapp/modules/expense/components/RequestablePrimaryCategorySelect'
import {
  RequestableSecondaryCategorySelect,
  type RequestableSecondaryCategorySelectValue,
} from '@webapp/modules/expense/components/RequestableSecondaryCategorySelect'
import { type Expense } from '@webapp/modules/expense/entities/Expense'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import messages from './messages'

export interface ExpenseReportFormSchema {
  title: string
  location: RequestablePrimaryCategorySelectValue | null
  department: RequestableSecondaryCategorySelectValue | null
  travel: AttachableTravelTicketSelectValue | null
  nextApprover: NextApproverSelectValue | null
  notes: string
}

export interface IExpenseReportFormProps {
  expense: Expense
  initialValues?: Partial<ExpenseReportFormSchema>
  canEditLocation: boolean
  canEditDepartment: boolean
  canEditTravelTicket: boolean
  canEditNextApprover: boolean
  statusBadges: { type: StatusBadgeStatus; text: string }[]
  isSubmitting: boolean
  submitLabel?: string
  formRef?: React.MutableRefObject<FormikProps<ExpenseReportFormSchema> | null>
  onSubmit: (values: ExpenseReportFormSchema) => void
  onEditItem: (item: ExpenseItem) => void
  onCancel: () => void
  onLocationChange?: (locationId: number) => void
  onDepartmentChange?: (departmentId: number) => void
  selectedLocationId?: Nullable<number>
  selectedDepartmentId?: Nullable<number>
  viewerRole: ExpenseViewerRoleEnum
}

export const ExpenseReportForm = ({
  expense,
  initialValues: initialValuesOverride,
  canEditLocation,
  canEditDepartment,
  canEditTravelTicket,
  canEditNextApprover,
  statusBadges,
  isSubmitting,
  submitLabel,
  formRef,
  onSubmit,
  onEditItem,
  onCancel,
  onLocationChange,
  onDepartmentChange,
  selectedLocationId,
  selectedDepartmentId,
  viewerRole,
}: IExpenseReportFormProps) => {
  const intl = useProcurifyIntl()
  const formatDate = useFormatDate()

  const accountCodeIds = expense.items.map(
    (item) => item.account.accountCode.id
  )
  const amount = expense.totalAmount

  const validationSchema = Yup.object<ExpenseReportFormSchema>({
    title: Yup.string()
      .required(intl.formatMessage(messages.titleRequired))
      .trim(),
    location: Yup.mixed()
      .nullable()
      .required(intl.formatMessage(messages.locationRequired)),
    department: Yup.mixed()
      .nullable()
      .required(intl.formatMessage(messages.departmentRequired)),
    travel: Yup.mixed().nullable(),
    nextApprover: canEditNextApprover
      ? Yup.mixed()
          .nullable()
          .test(
            'nextApprover-required',
            intl.formatMessage(messages.nextApproverRequired),
            (value) => !!value
          )
      : Yup.mixed().nullable(),
    notes: Yup.string().max(
      2000,
      intl.formatMessage(messages.purposeMaxLength)
    ),
  })

  const initialTravel = expense.travel?.id
    ? {
        id: expense.travel.id,
        displayLabel: intl.formatMessage(messages.travelTicketValue, {
          id: expense.travel.id,
          purpose: expense.travel.purpose,
        }),
      }
    : null
  const resolvedInitialValues: ExpenseReportFormSchema = {
    title: initialValuesOverride?.title ?? expense.title,
    location:
      initialValuesOverride?.location === undefined
        ? {
            id: expense.primaryCategory.id,
            name: expense.primaryCategory.name,
          }
        : initialValuesOverride.location,
    department:
      initialValuesOverride?.department === undefined
        ? {
            id: expense.secondaryCategory.id,
            name: expense.secondaryCategory.name,
          }
        : initialValuesOverride.department,
    travel:
      initialValuesOverride?.travel === undefined
        ? initialTravel
        : initialValuesOverride.travel,
    nextApprover: initialValuesOverride?.nextApprover ?? null,
    notes: initialValuesOverride?.notes ?? expense.description,
  }

  const formik = useFormik<ExpenseReportFormSchema>({
    initialValues: resolvedInitialValues,
    validationSchema,
    onSubmit,
  })

  if (formRef) {
    formRef.current = formik
  }

  const getMenuListItems = (
    item: ExpenseItem,
    _rowIndex: number
  ): RowCellMenuListItemProps[] => [
    {
      text: intl.formatMessage(messages.editExpenseLineMenuLabel),
      onClick: () => onEditItem(item),
    },
  ]

  const effectiveLocationId =
    selectedLocationId === undefined
      ? expense.primaryCategory.id
      : selectedLocationId
  const effectiveDepartmentId =
    selectedDepartmentId === undefined
      ? expense.secondaryCategory.id
      : selectedDepartmentId

  const { setFieldValue, values } = formik

  const isDepartmentInitialized = useRef(true)
  useEffect(() => {
    if (isDepartmentInitialized.current) {
      isDepartmentInitialized.current = false
      return
    }
    const departmentId = values.department?.id
    if (departmentId != null) {
      onDepartmentChange?.(departmentId)
    }
    setFieldValue('nextApprover', null)
  }, [values.department?.id, onDepartmentChange, setFieldValue])

  const isLocationInitialized = useRef(true)
  useEffect(() => {
    if (isLocationInitialized.current) {
      isLocationInitialized.current = false
      return
    }
    const locationId = values.location?.id
    if (locationId != null) {
      onLocationChange?.(locationId)
    }
    setFieldValue('department', null)
  }, [values.location?.id, onLocationChange, setFieldValue])

  const isAmountInitialized = useRef(true)
  useEffect(() => {
    if (isAmountInitialized.current) {
      isAmountInitialized.current = false
      return
    }

    setFieldValue('nextApprover', null)
  }, [amount, setFieldValue])

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space size='xlarge'>
          <Space size='large'>
            {/* Header and Form Actions Section */}
            <Space direction='horizontal' justify='space-between' align='start'>
              <Space>
                <Space direction='horizontal' align='center'>
                  <Header as='h5'>
                    {intl.formatMessage(messages.expenseReportHeading, {
                      id: expense.id,
                    })}
                  </Header>
                  {statusBadges.map((badge) => (
                    <StatusBadge
                      key={badge.text}
                      type={badge.type}
                      text={badge.text}
                    />
                  ))}
                </Space>
                <Space direction='horizontal' align='center'>
                  <Space.Item minWidth={500}>
                    <Form.TextField
                      id='title'
                      name='title'
                      fullWidth
                      placeholder={intl.formatMessage(
                        messages.titlePlaceholder
                      )}
                      required
                      disabled={isSubmitting}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Space.Item>
                </Space>
              </Space>

              <Space
                direction='horizontal'
                justify='end'
                align='center'
                size='medium'
              >
                <TertiaryButton
                  type='button'
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {intl.formatMessage(messages.cancelButton)}
                </TertiaryButton>
                <PrimaryButton type='submit' disabled={isSubmitting}>
                  {submitLabel ?? intl.formatMessage(messages.saveButton)}
                </PrimaryButton>
              </Space>
            </Space>

            <Space direction='horizontal'>
              <Space.Item width={1 / 2}>
                <Descriptions columns={1}>
                  <Descriptions.Item
                    label={intl.formatMessage(messages.requestedByLabel)}
                  >
                    <Label
                      component={
                        <UserAvatar
                          shadow={false}
                          url={expense.requester.profileImage ?? undefined}
                          size='xsmall'
                          circular
                        />
                      }
                    >
                      {expense.requester.fullName}
                    </Label>
                  </Descriptions.Item>

                  {canEditLocation ? (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.locationLabel)}
                    >
                      <WithFormControlSelect
                        name='location'
                        id='location'
                        required
                      >
                        <RequestablePrimaryCategorySelect
                          value={formik.values.location}
                          isDisabled={isSubmitting}
                        />
                      </WithFormControlSelect>
                    </Descriptions.Item>
                  ) : (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.locationLabel)}
                    >
                      {expense.primaryCategory.name}
                    </Descriptions.Item>
                  )}

                  {canEditDepartment ? (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.departmentLabel)}
                    >
                      <WithFormControlSelect
                        name='department'
                        id='department'
                        required
                      >
                        <RequestableSecondaryCategorySelect
                          locationId={effectiveLocationId}
                          value={formik.values.department}
                          isDisabled={
                            isSubmitting || effectiveLocationId === null
                          }
                        />
                      </WithFormControlSelect>
                    </Descriptions.Item>
                  ) : (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.departmentLabel)}
                    >
                      {expense.secondaryCategory.name}
                    </Descriptions.Item>
                  )}

                  {canEditTravelTicket && (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.travelTicketLabel)}
                    >
                      <AttachableTravelTicketSelect
                        userId={expense.requester.id}
                        value={formik.values.travel}
                        onChange={(option) =>
                          formik.setFieldValue('travel', option)
                        }
                        isClearable
                        isDisabled={isSubmitting}
                      />
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Space.Item>

              <Space.Item width={1 / 2}>
                <Descriptions columns={1}>
                  {canEditNextApprover && (
                    <Descriptions.Item
                      label={intl.formatMessage(messages.nextApproverLabel)}
                    >
                      <WithFormControlSelect
                        name='nextApprover'
                        id='nextApprover'
                        required
                      >
                        <NextApproverSelect
                          departmentId={effectiveDepartmentId}
                          accountCodeIds={accountCodeIds}
                          amount={amount}
                          isDisabled={
                            isSubmitting || effectiveDepartmentId === null
                          }
                        />
                      </WithFormControlSelect>
                    </Descriptions.Item>
                  )}

                  <Descriptions.Item
                    label={intl.formatMessage(messages.submittedLabel)}
                  >
                    {formatDate(expense.submittedDate, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={intl.formatMessage(messages.lastModifiedLabel)}
                  >
                    {formatDate(expense.dateLastModified, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </Descriptions.Item>
                </Descriptions>
              </Space.Item>
            </Space>
          </Space>

          <Space>
            <Header as='h4'>
              {intl.formatMessage(messages.expensePurposeHeading)}
            </Header>
            <Form.TextField
              id='notes'
              name='notes'
              fullWidth
              multiline
              minRows={4}
              placeholder={intl.formatMessage(messages.purposePlaceholder)}
              inputProps={{ maxLength: 2000 }}
              disabled={isSubmitting}
            />
          </Space>

          <ExpenseItemTable
            items={expense.items}
            menuListItems={getMenuListItems}
            onAttachmentClick={(item) => onEditItem(item)}
            viewerRole={viewerRole}
          />

          <Space direction='vertical'>
            <Space direction='horizontal' justify='end'>
              <CostSummary>
                <CostSummary.Cost>
                  <th id='footer-cost-summary-subtotal'>
                    {intl.formatMessage(messages.subtotalLabel)}
                  </th>
                  <td>
                    <Price
                      price={expense.subtotal}
                      currency={expense.currency.name}
                      fontSize={14}
                      aria-labelledby='footer-cost-summary-subtotal'
                    />
                  </td>
                </CostSummary.Cost>
                <CostSummary.Cost>
                  <th id='footer-cost-summary-tax'>
                    {intl.formatMessage(messages.taxLabel)}
                  </th>
                  <td>
                    <Price
                      price={expense.taxTotal}
                      currency={expense.currency.name}
                      fontSize={14}
                      aria-labelledby='footer-cost-summary-tax'
                    />
                  </td>
                </CostSummary.Cost>
                <CostSummary.Total>
                  <th id='footer-cost-summary-total'>
                    {intl.formatMessage(messages.totalLabel)}
                  </th>
                  <td>
                    <Price
                      price={expense.totalAmount}
                      currency={expense.currency.name}
                      fontSize={18}
                      aria-labelledby='footer-cost-summary-total'
                    />
                  </td>
                </CostSummary.Total>
                <CostSummary.Cost>
                  <th id='footer-cost-summary-reimbursable'>
                    {intl.formatMessage(messages.reimbursableLabel)}
                  </th>
                  <td>
                    <Price
                      price={expense.reimbursableAmount}
                      currency={expense.currency.name}
                      fontSize={14}
                      aria-labelledby='footer-cost-summary-reimbursable'
                    />
                  </td>
                </CostSummary.Cost>
              </CostSummary>
            </Space>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}
